import React from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { Card, CardSubtitle } from 'reactstrap';
import { useFetch, PseudoStates, useSession } from '@cordata/buffet';
import { UncontrolledTable, SimpleCollapse } from '~/components';
import formatPhone from '~/utils/format/phone';
import formatDate from '~/utils/format/date';

const SecondaryEndReasonCell = ({ original }) => {
  const { debugMode } = useSession();

  if (
    original.attributes.primaryEndReason !== original.attributes.secondaryEndReason ||
    debugMode
  ) {
    return (
      <span>{original.attributes.secondaryEndReason}</span>
    );
  }

  return null;
};

SecondaryEndReasonCell.propTypes = {
  original: PropTypes.shape().isRequired,
};

const PlacementHistory = () => {
  const params = useParams();

  return (
    <UncontrolledTable
      name="placement-history"
      endpoint={{
        url: 'data/public/list-view-cs-tbl-source-placement-history',
          params: {
            patient_uid: params.patient_id,
            'all-data': '1',
          },
      }}
      className="mt-2"
      defaultSorted={[{ id: 'beginDateDt', desc: true }]}
      columns={[
        {
          id: 'county',
          Header: 'County',
          accessor: 'attributes.county',
        },
        {
          id: 'placementType',
          Header: 'Placement Type',
          accessor: 'attributes.placementType',
        },
        {
          id: 'providerName',
          Header: 'Provider Name',
          accessor: 'attributes.providerName',
        },
        {
          id: 'beginDateDt',
          Header: 'Begin Date',
          accessor: 'attributes.beginDateDt',
          cellType: 'date',
        },
        {
          id: 'endDateDt',
          Header: 'End Date',
          accessor: 'attributes.endDateDt',
          cellType: { type: 'date', emptyValue: 'Current' },
        },
        {
          id: 'primaryEndReason',
          Header: 'Primary End Reason',
          accessor: 'attributes.primaryEndReason',
        },
        {
          id: 'secondaryEndReason',
          Header: 'Secondary End Reason',
          accessor: 'attributes.secondaryEndReason',
          Cell: SecondaryEndReasonCell,
        },
      ]}
    />
  );
};

const Placement = () => {
  const params = useParams();

  const [sourceChildStatus, sourceChild] = useFetch(
    'data/public/list-view-cs-tbl-source-child',
    {
      params: {
        patient_uid: params.patient_id,
        sort: '-custodyStartDateDt',
        'all-data': '1',
      },
      initialState: [],
      instance: 'p',
    },
  );

  return (
    <Card body className="mb-3">
      {sourceChildStatus === 'success' ? (
        sourceChild.map(sc => (
          <>
            <CardSubtitle className="text-primary">Reporting County</CardSubtitle>

            {sc.attributes.county}

            <CardSubtitle className="text-primary mt-3">Placement</CardSubtitle>
            {sc.attributes.placementType}
            <br />
            {sc.attributes.providerName}
            <br />
            <a href={`tel:${sc.attributes.providerContact}`}>
              {formatPhone(sc.attributes.providerContact)}
            </a>
            <br />
            Placement begin date:{' '}
            {formatDate(sc.attributes.placementBeginDt, 'MM/DD/YYYY', 'N/A')}
            <br />
            Date placement entered:{' '}
            {formatDate(sc.attributes.placementEnteredDt, 'MM/DD/YYYY', 'N/A')}
            <CardSubtitle className="text-primary mt-3">
              Placement Agency
            </CardSubtitle>
            {sc.attributes.providerAgency}
            <br />
            <a href={`tel:${sc.attributes.providerAgencyContact}`}>
              {formatPhone(sc.attributes.providerAgencyContact)}
            </a>

            {(sc.attributes.leaveDate || sc.attributes.leaveType) && (
              <>
                <CardSubtitle className="text-primary mt-3">
                  Placement Leave
                </CardSubtitle>
                Begin date: {formatDate(sc.attributes.leaveDate, 'MM/DD/YYYY', 'N/A')}
                {sc.attributes.leaveType && (
                  <>
                    <br />
                    Leave type: {sc.attributes.leaveType}
                  </>
                )}
                {sc.attributes.leaveProviderId && (
                  <>
                    <br />
                    Provider ID: {sc.attributes.leaveProviderId}
                  </>
                )}
                {sc.attributes.leaveProviderName && (
                  <>
                    <br />
                    Provider name: {sc.attributes.leaveProviderName}
                  </>
                )}
                {sc.attributes.leaveProviderContact && (
                  <>
                    <br />
                    Provider contact: {sc.attributes.leaveProviderContact}
                  </>
                )}
                {sc.attributes.leaveProviderAddress && (
                  <>
                    <br />
                    Provider address: {sc.attributes.leaveProviderAddress}
                  </>
                )}
              </>
            )}
          </>
        ))
      ) : (
        <PseudoStates status={sourceChildStatus} />
      )}

      <SimpleCollapse
        title="History"
        body={<PlacementHistory />}
        className="mt-3"
      />
    </Card>
  );
};

export default Placement;
